import { Check, ChevronDown, Globe, LayoutList } from "lucide-react";
import { useState, useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { countries } from "@/data/countries";
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";
import { useLocalizedLocations, LocalizedLocation } from "@/hooks/useLocalizedLocations";
import { INTERNATIONAL_LOCATION_ID } from "@/constants/config";
import { useQueryClient } from "@tanstack/react-query";
import { createSearchFilter } from "@/lib/commandUtils";
import { FORM_CONTROLS, TYPOGRAPHY, INTERACTION_STATES } from "@/design-system";
import { DELAYS } from '@/constants/timing';

export interface LocationPickerProps {
  /** Current selected value: location_id, 'GLOBAL', 'all', or null (for International Only in admin) */
  value: string | null;
  /** Callback when a location is selected */
  onSelect: (locationId: string | null) => void;
  /** Show "International" option (default: true) */
  showInternational?: boolean;
  /** Show "All Locations" option for filters (default: false) */
  showAllLocations?: boolean;
  /** Show "International Only" option for admin filters where null means international (default: false) */
  showInternationalOnly?: boolean;
  /** Optional: restrict to only these location IDs */
  restrictToLocationIds?: string[];
  /** Button variant */
  variant?: 'ghost' | 'outline' | 'default';
  /** Button size */
  size?: 'sm' | 'default' | 'icon';
  /** Popover alignment */
  align?: 'start' | 'end' | 'center';
  /** Additional class names */
  className?: string;
  /** Custom trigger content (overrides default) */
  customTrigger?: React.ReactNode;
  /** Show flag in trigger */
  showFlag?: boolean;
  /** Show name in trigger */
  showName?: boolean;
  /** Trigger width class */
  triggerClassName?: string;
  /** Label for "all locations" option */
  allLocationsLabel?: string;
  /** Label for "international" option */
  internationalLabel?: string;
  /** Placeholder when no location selected (admin use case) */
  placeholder?: string;
  /** Admin mode: show English-only labels sorted by English name (default: false) */
  isAdmin?: boolean;
  /** Values that should be treated as null/empty (e.g., [0, '0', 'GLOBAL'] for country fields) */
  treatAsEmpty?: (string | number)[];
}

export const LocationPicker = ({
  value,
  onSelect,
  showInternational = true,
  showAllLocations = false,
  showInternationalOnly = false,
  restrictToLocationIds,
  variant = 'ghost',
  size = 'sm',
  align = 'end',
  className,
  customTrigger,
  showFlag = true,
  showName = true,
  triggerClassName,
  allLocationsLabel,
  internationalLabel,
  placeholder,
  isAdmin = false,
  treatAsEmpty,
}: LocationPickerProps) => {
  const { lCommon } = useLocalizations();
  const { locations, isLoading } = useLocalizedLocations({ sortByEnglish: isAdmin });
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [optimisticValue, setOptimisticValue] = useState<string | null>(null);
  const searchFilter = useMemo(() => createSearchFilter(), []);
  const listRef = useRef<HTMLDivElement>(null);

  // Pre-fetch location data on hover for instant switching
  const handleLocationHover = useCallback((locationId: string) => {
    // Pre-fetch location-related queries
    queryClient.prefetchQuery({
      queryKey: ['location-page-data', locationId],
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
    queryClient.prefetchQuery({
      queryKey: ['localized-slug', locationId],
      staleTime: 10 * 60 * 1000, // 10 minutes
    });
  }, [queryClient]);

  // Normalize value: treat specified values as empty
  const normalizedValue = treatAsEmpty?.some(v => String(v) === String(value)) ? null : value;
  
  // Use optimistic value if set, otherwise real value
  const displayValue = optimisticValue ?? normalizedValue;

  // Filter locations if restrictToLocationIds is provided
  const filteredLocations = restrictToLocationIds?.length
    ? locations.filter(loc => restrictToLocationIds.includes(loc.id))
    : locations;

  // Find current location by id (use normalized value)
  const currentLocationData = locations.find((l) => l.id === normalizedValue);
  // For flag, look up by iso from the location data
  const currentCountry = currentLocationData?.iso 
    ? countries.find((c) => c.code === currentLocationData.iso)
    : null;

  const getDisplayName = () => {
    if (displayValue === 'all') return allLocationsLabel || lCommon('labels.all.locations', { defaultValue: 'Locations' });
    if (displayValue === INTERNATIONAL_LOCATION_ID || displayValue === 'GLOBAL') return internationalLabel || lCommon('labels.international', { defaultValue: 'International' });
    if (displayValue === null && showInternationalOnly) return internationalLabel || lCommon('labels.international_only', { defaultValue: 'International' });
    
    // When no value: use placeholder, or "International" only if showInternational is true
    if (!displayValue) {
      if (placeholder) return placeholder;
      return showInternational ? (internationalLabel || lCommon('labels.international', { defaultValue: 'International' })) : lCommon('labels.select_country', { defaultValue: 'Select Country' });
    }
    
    // For optimistic updates, find the location data
    const locationData = locations.find((l) => l.id === displayValue);
    
    // Admin: use English short/name for consistency. Frontend: use translated name
    if (isAdmin) {
      return locationData?.short || locationData?.english || currentLocationData?.short || currentLocationData?.english || placeholder || lCommon('labels.select_country', { defaultValue: 'Select Country' });
    }
    return locationData?.name || currentLocationData?.name || placeholder || lCommon('labels.select_country', { defaultValue: 'Select Country' });
  };

  const getDisplayFlag = () => {
    if (displayValue === 'all') return null;
    if (displayValue === INTERNATIONAL_LOCATION_ID || displayValue === 'GLOBAL') return null;
    // No value: only show globe if showInternational is true
    if (!displayValue) return showInternational ? null : null;
    // For optimistic updates, find the country data
    const locationData = locations.find((l) => l.id === displayValue);
    const countryData = locationData?.iso ? countries.find(c => c.code === locationData.iso) : currentCountry;
    return countryData?.flag || null;
  };

  const handleSelect = (selectedValue: string | null) => {
    // Optimistic UI: show new value immediately
    setOptimisticValue(selectedValue);
    onSelect(selectedValue);
    // Close popover after selection
    setOpen(false);
    // Scroll list to top so user sees their selection on next open
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    // Clear optimistic state after context update completes
    setTimeout(() => setOptimisticValue(null), DELAYS.DEBOUNCE);
  };

  if (isLoading) {
    return (
      <Button variant={variant} size={size} className={cn("text-sm gap-1", className)} disabled>
        {showFlag && <span className="text-base">🌍</span>}
        {showName && <span>...</span>}
      </Button>
    );
  }

  const displayFlag = getDisplayFlag();
  const displayName = getDisplayName();

  const renderTrigger = () => {
    if (customTrigger) return customTrigger;

    return (
      <Button 
        variant={variant} 
        size={size} 
        role="combobox"
        aria-expanded={open}
        aria-label={lCommon('aria.select_location', { defaultValue: 'Select Location' })}
        className={cn(
          TYPOGRAPHY.filterTrigger,
          INTERACTION_STATES.navButton.interactive,
          triggerClassName
        )}
      >
        <>
          {showFlag && displayFlag && <span className="text-base mr-1">{displayFlag}</span>}
          {!showFlag && <Globe className="h-3.5 w-3.5 mr-1.5 opacity-60 shrink-0" />}
          {showFlag && !displayFlag && showInternational && (
            <Globe className="h-4 w-4 mr-1 text-muted-foreground" />
          )}
          {showName && <span className="truncate">{displayName}</span>}
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-auto" />
        </>
      </Button>
    );
  };

  const renderLocationItem = (location: LocalizedLocation, isSelected: boolean) => {
    const country = countries.find(c => c.code === location.iso);
    
    // Admin: English name only. Frontend: navigation language name only (no secondary line)
    const itemDisplayName = isAdmin 
      ? location.english 
      : location.name; // name is already the translated/navigation language name

    // Keywords for search: include native, english, and short name for better matching
    const searchKeywords = [location.native, location.english, location.short || ''].filter(Boolean);

    return (
      <CommandItem
        key={location.id}
        value={itemDisplayName}
        keywords={searchKeywords}
        onSelect={() => handleSelect(location.id)}
        onMouseEnter={() => handleLocationHover(location.id)}
        className={cn(FORM_CONTROLS.command.item, isSelected && "bg-muted")}
      >
        <span className="mr-2 w-5 inline-flex items-center justify-center flex-shrink-0">{country?.flag || "🌍"}</span>
        <div className="flex-1 min-w-0">
          <div className="truncate">{itemDisplayName}</div>
        </div>
        {isSelected && (
          <span className="ml-auto flex-shrink-0 bg-success rounded-full p-0.5">
            <Check className="h-3 w-3 text-success-foreground" strokeWidth={3} />
          </span>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover 
      open={open} 
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        {renderTrigger()}
      </PopoverTrigger>
      <PopoverContent align={align} className={cn(FORM_CONTROLS.command.popoverWide, className)}>
        <Command filter={searchFilter}>
          <CommandInput 
            placeholder={lCommon('search.locations.placeholder', { defaultValue: 'Search locations' })} 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList ref={listRef}>
            <CommandEmpty>{lCommon('empty.no_location_found', { defaultValue: 'No location found.' })}</CommandEmpty>
            <CommandGroup>
              {/* Only show special items when NOT searching */}
              {/* All Locations option */}
              {!search && showAllLocations && (() => {
                const isSelected = displayValue === 'all';
                return (
                  <CommandItem
                    value="all-locations"
                    onSelect={() => handleSelect('all')}
                    className={cn(FORM_CONTROLS.command.item, isSelected && "bg-muted")}
                  >
                    <span className="mr-2 w-5 inline-flex items-center justify-center flex-shrink-0">
                      <LayoutList className="h-4 w-4 text-muted-foreground" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{allLocationsLabel || lCommon('labels.all.locations', { defaultValue: 'Locations' })}</div>
                    </div>
                    {isSelected && (
                      <span className="ml-auto flex-shrink-0 bg-success rounded-full p-0.5">
                        <Check className="h-3 w-3 text-success-foreground" strokeWidth={3} />
                      </span>
                    )}
                  </CommandItem>
                );
              })()}

              {/* Separator before International options */}
              {!search && showAllLocations && (showInternational || showInternationalOnly) && (
                <CommandSeparator className="my-1" />
              )}

              {/* International option - only when not searching */}
              {!search && showInternational && (() => {
                const isSelected = displayValue === INTERNATIONAL_LOCATION_ID || displayValue === 'GLOBAL';
                return (
                  <CommandItem
                    value="international"
                    onSelect={() => handleSelect(INTERNATIONAL_LOCATION_ID)}
                    className={cn(FORM_CONTROLS.command.item, isSelected && "bg-muted")}
                  >
                    <span className="mr-2 w-5 inline-flex items-center justify-center flex-shrink-0">
                      <Globe className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{internationalLabel || lCommon('labels.international', { defaultValue: 'International' })}</div>
                    </div>
                    {isSelected && (
                      <span className="ml-auto flex-shrink-0 bg-success rounded-full p-0.5">
                        <Check className="h-3 w-3 text-success-foreground" strokeWidth={3} />
                      </span>
                    )}
                  </CommandItem>
                );
              })()}

              {/* International Only option for admin - only when not searching */}
              {!search && showInternationalOnly && (() => {
                const isSelected = displayValue === null;
                return (
                  <CommandItem
                    value="international-only"
                    onSelect={() => handleSelect(null)}
                    className={cn(FORM_CONTROLS.command.item, isSelected && "bg-muted")}
                  >
                    <span className="mr-2 w-5 inline-flex items-center justify-center flex-shrink-0">🌐</span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{lCommon('labels.international_only', { defaultValue: 'International' })}</div>
                    </div>
                    {isSelected && (
                      <span className="ml-auto flex-shrink-0 bg-success rounded-full p-0.5">
                        <Check className="h-3 w-3 text-success-foreground" strokeWidth={3} />
                      </span>
                    )}
                  </CommandItem>
                );
              })()}

              {/* Separator before locations list */}
              {!search && (showAllLocations || showInternational || showInternationalOnly) && (
                <CommandSeparator className="my-1" />
              )}

              {/* Current selected location at top (when not searching and a specific country is selected) */}
              {!search && displayValue && displayValue !== 'all' && displayValue !== INTERNATIONAL_LOCATION_ID && displayValue !== 'GLOBAL' && (() => {
                const currentLoc = filteredLocations.find(loc => loc.id === displayValue);
                if (currentLoc) {
                  return (
                    <>
                      {renderLocationItem(currentLoc, true)}
                      <CommandSeparator className="my-1" />
                    </>
                  );
                }
                return null;
              })()}

              {/* All locations in natural alphabetical order (exclude current from main list if shown above) */}
              {filteredLocations
                .filter(loc => {
                  // When not searching and current is shown at top, exclude it from main list
                  if (!search && displayValue && displayValue !== 'all' && displayValue !== INTERNATIONAL_LOCATION_ID && displayValue !== 'GLOBAL') {
                    return loc.id !== displayValue;
                  }
                  return true;
                })
                .map((loc) => renderLocationItem(loc, loc.id === displayValue))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
